import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  FileWarning,
  Menu,
  Upload
} from 'lucide-react';
import './App.css';
import './TasksPage.css';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import { logout as endSession } from './auth';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
const API_BASE_URL = API_ROOT ? `${API_ROOT}/api` : '/api';

const STATUS_LABELS = {
  inward: 'Inward',
  in_progress: 'In Progress',
  outward: 'Outward',
  completed: 'Completed'
};

export default function MyTasksPage() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState({
    missingDrawings: [],
    needsRevision: [],
    awaitingAdminReview: [],
    counts: {}
  });

  useEffect(() => {
    if (role !== 'architect') {
      navigate('/dashboard', { replace: true });
    }
  }, [role, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/tasks/mine`);
      setTasks(data || {});
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const openDrawingModal = (clientDbId) => {
    navigate('/dashboard', { state: { editClientId: clientDbId } });
  };

  return (
    <div className="App">
      <ToastContainer theme="light" position="top-right" autoClose={3000} />
      <div className="app-shell">
        <AppSidebar
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          active="tasks"
          onLogout={() => endSession()}
        />
        <div className="app-main">
          <header className="app-topbar">
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn-outline sidebar-toggle"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={18} />
                Menu
              </button>
              <h1 className="app-topbar-title">My Tasks</h1>
            </div>
            <div className="app-topbar-actions">
              <NotificationBell />
            </div>
          </header>

          <div className="container tasks-page">
            <div className="tasks-summary">
              <div className="tasks-summary-card">
                <Upload size={18} />
                <div>
                  <strong>{tasks.counts?.missingDrawings || 0}</strong>
                  <span>Missing drawings</span>
                </div>
              </div>
              <div className="tasks-summary-card tasks-summary-card--warn">
                <FileWarning size={18} />
                <div>
                  <strong>{tasks.counts?.needsRevision || 0}</strong>
                  <span>Needs revision</span>
                </div>
              </div>
              <div className="tasks-summary-card tasks-summary-card--muted">
                <CheckCircle2 size={18} />
                <div>
                  <strong>{tasks.counts?.awaitingAdminReview || 0}</strong>
                  <span>Awaiting admin review</span>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="card"><p>Loading tasks…</p></div>
            ) : (
              <>
                <section className="card tasks-section">
                  <header className="tasks-section-head">
                    <ClipboardList size={18} />
                    <div>
                      <h2>Upload Architect Drawing</h2>
                      <p>Assigned clients that still need a drawing.</p>
                    </div>
                  </header>
                  {(tasks.missingDrawings || []).length === 0 ? (
                    <p className="tasks-empty">All assigned clients have at least one drawing.</p>
                  ) : (
                    <ul className="tasks-list">
                      {tasks.missingDrawings.map((row) => (
                        <li key={row.id} className="tasks-item">
                          <div>
                            <strong>{row.client_name}</strong>
                            <span>{row.client_id} · {row.customer_area} · {STATUS_LABELS[row.status] || row.status}</span>
                          </div>
                          <button type="button" className="btn btn-primary" onClick={() => openDrawingModal(row.id)}>
                            Upload drawing
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="card tasks-section">
                  <header className="tasks-section-head">
                    <AlertTriangle size={18} />
                    <div>
                      <h2>Needs revision</h2>
                      <p>Admin requested changes on these drawings.</p>
                    </div>
                  </header>
                  {(tasks.needsRevision || []).length === 0 ? (
                    <p className="tasks-empty">No drawings marked for revision.</p>
                  ) : (
                    <ul className="tasks-list">
                      {tasks.needsRevision.map((row) => (
                        <li key={row.attachment_id} className="tasks-item">
                          <div>
                            <strong>{row.attach_name || 'Architect Drawing'}</strong>
                            <span>
                              {row.client_name} · {row.client_id}
                              {row.remark ? ` — ${row.remark}` : ''}
                            </span>
                          </div>
                          <div className="tasks-item-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => navigate(`/clients/${row.client_db_id}`)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => openDrawingModal(row.client_db_id)}
                            >
                              Upload revision
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="card tasks-section">
                  <header className="tasks-section-head">
                    <CheckCircle2 size={18} />
                    <div>
                      <h2>Awaiting admin review</h2>
                      <p>Drawings you uploaded that are still pending approval.</p>
                    </div>
                  </header>
                  {(tasks.awaitingAdminReview || []).length === 0 ? (
                    <p className="tasks-empty">Nothing waiting on admin right now.</p>
                  ) : (
                    <ul className="tasks-list">
                      {tasks.awaitingAdminReview.map((row) => (
                        <li key={row.attachment_id} className="tasks-item">
                          <div>
                            <strong>{row.attach_name || 'Architect Drawing'}</strong>
                            <span>{row.client_name} · {row.client_id}</span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(`/clients/${row.client_db_id}`)}
                          >
                            Open client
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
